import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Autocomplete, Box, FormControlLabel, IconButton, InputAdornment, MenuItem, Stack, Switch,
    TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import { ConfigCard } from '../style/cards';

// Visual tile picker for the brands page layout. CSS-only pictograms keep
// the bundle lean (no extra icon imports).
const LAYOUTS = [
    { value: 'continuous',  i18n_label: 'cfg_brands_layout_continuous',          fallback_label: 'Continuous list',
                            i18n_desc:  'cfg_brands_layout_continuous_desc',     fallback_desc:  'All brands shown together' },
    { value: 'grouped',     i18n_label: 'cfg_brands_layout_grouped',             fallback_label: 'Grouped by letter',
                            i18n_desc:  'cfg_brands_layout_grouped_desc',        fallback_desc:  'A, B, C… headers with brands listed under each' },
    { value: 'multiCol',    i18n_label: 'cfg_brands_layout_multi',               fallback_label: 'Grouped by letter (multi-column)',
                            i18n_desc:  'cfg_brands_layout_multi_desc',          fallback_desc:  'Letters arranged side by side in columns' },
];
const LayoutTile = ({ value }) => {
    const block = { backgroundColor: 'currentColor', borderRadius: 0.4 };
    if (value === 'continuous') {
        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 18px)', gap: 0.4 }}>
                {Array.from({ length: 9 }).map((_, i) => <Box key={i} sx={{ ...block, height: 10, opacity: 0.7 }} />)}
            </Box>
        );
    }
    if (value === 'grouped') {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {['A','B','C'].map(letter => (
                    <Box key={letter} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Box sx={{ fontSize: 10, fontWeight: 700, color: 'currentColor', width: 10 }}>{letter}</Box>
                        <Box sx={{ ...block, width: 36, height: 6, opacity: 0.7 }} />
                    </Box>
                ))}
            </Box>
        );
    }
    // multiCol — letters in a 3×2 grid
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.6 }}>
            {['A','B','C','D','E','F'].map(letter => (
                <Box key={letter} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <Box sx={{ fontSize: 8, fontWeight: 700 }}>{letter}</Box>
                    <Box sx={{ ...block, width: 14, height: 4, opacity: 0.7 }} />
                </Box>
            ))}
        </Box>
    );
};

// ────────── Reusable per-row helpers ──────────

// Generic row: label + helper on the left, control on the right. When `disabled`
// is true the row dims and an extra "Only available when …" hint appears.
const SettingRow = ({ label, help, control, disabled = false, disabledHint }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, opacity: disabled ? 0.45 : 1, py: 0.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>{help}</Typography>}
            {disabled && disabledHint && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block', mt: 0.25, lineHeight: 1.3 }}>
                    {disabledHint}
                </Typography>
            )}
        </Box>
        <Box sx={{ flexShrink: 0, pt: 0.25, pointerEvents: disabled ? 'none' : 'auto' }}>{control}</Box>
    </Box>
);

// One sub-block in the Text styles card (Brand name / Alphabet navigation /
// Letter heading). Header + helper, then a px input, then a colour swatch +
// Bold/Italic/Underline toggles.
const TextStyleBlock = ({ label, help, value = {}, onChange }) => {
    const set = (k) => (v) => onChange && onChange({ ...value, [k]: v });
    const togglePill = (k) => () => set(k)(!value[k]);
    return (
        <Box sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none', pb: 0 } }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>{help}</Typography>}
            <TextField
                type="number" size="small" fullWidth
                value={value.size ?? 14}
                onChange={(e) => set('size')(Math.max(8, parseInt(e.target.value) || 0))}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.75rem' } }}>px</InputAdornment> }}
                sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' } }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                <Box component="label" sx={{ position: 'relative', width: 28, height: 28, borderRadius: 0.75, border: '1px solid rgba(0,0,0,0.18)', backgroundColor: value.color || '#000000', cursor: 'pointer', flexShrink: 0 }}>
                    <input type="color" value={value.color || '#000000'} onChange={(e) => set('color')(e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                    <PillIconButton active={value.bold}      onClick={togglePill('bold')}><FormatBoldIcon       fontSize="small" /></PillIconButton>
                    <PillIconButton active={value.italic}    onClick={togglePill('italic')}><FormatItalicIcon     fontSize="small" /></PillIconButton>
                    <PillIconButton active={value.underline} onClick={togglePill('underline')}><FormatUnderlinedIcon fontSize="small" /></PillIconButton>
                </Box>
            </Box>
        </Box>
    );
};
const PillIconButton = ({ active, onClick, children }) => (
    <IconButton
        size="small"
        onClick={onClick}
        sx={{
            border: '1px solid',
            borderColor: active ? 'primary.main' : 'divider',
            borderRadius: 0.75,
            color: active ? 'primary.main' : 'text.primary',
            backgroundColor: active ? 'rgba(25,118,210,0.08)' : 'transparent',
            width: 30, height: 30,
        }}
    >{children}</IconButton>
);

// ────────── Tab ──────────

export const BRANDS_CONFIG_DEFAULT = {
    whatToShow:  { onlyOnline: false, navigationCategory: '' },
    layout: 'grouped',
    settings: {
        displayContinuous: 'logo',
        displayGrouped:    'text',
        showLettersWithoutBrands: false,
        letterColumns: 4,
        brandsPerLetter: 7,
    },
    textStyles: {
        desktop: {
            brandName:    { size: 14, color: '#111111', bold: false, italic: false, underline: false },
            alphabetNav:  { size: 12, color: '#111111', bold: false, italic: false, underline: false },
            letterHeading:{ size: 18, color: '#111111', bold: true,  italic: false, underline: false },
        },
        mobile: {
            brandName:    { size: 12, color: '#111111', bold: false, italic: false, underline: false },
            alphabetNav:  { size: 11, color: '#111111', bold: false, italic: false, underline: false },
            letterHeading:{ size: 16, color: '#111111', bold: true,  italic: false, underline: false },
        },
    },
};

const MerkenTab = ({ theme, t, config: configProp, onConfigChange, shopFieldLabels = {} }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);

    // Controlled by the parent (loaded from BRANDS_* settings, saved on the
    // global Save) when wired; otherwise a local fallback keeps it usable.
    const controlled = configProp != null && typeof onConfigChange === 'function';
    const [localConfig, setLocalConfig] = useState(BRANDS_CONFIG_DEFAULT);
    const config = controlled ? configProp : localConfig;
    const setConfig = controlled ? onConfigChange : setLocalConfig;

    const setSection = (sec) => (val) => setConfig(prev => ({ ...prev, [sec]: { ...(prev[sec] || {}), ...val } }));
    const setNested  = (sec, sub) => (val) => setConfig(prev => ({
        ...prev,
        [sec]: {
            ...(prev[sec] || {}),
            [sub]: { ...((prev[sec] || {})[sub] || {}), ...val },
        },
    }));

    // Conditional disable rules — match the schema's "only available when …".
    const isContinuous = config.layout === 'continuous';
    const isGrouped    = config.layout === 'grouped' || config.layout === 'multiCol';
    const isMultiCol   = config.layout === 'multiCol';

    // Navigation category — None + category_1..20, labelled with the shop's own
    // category labels (kasvars) when available. The stored value is the brand
    // category field (e.g. "category_1"); any value not in the range is still
    // shown so an existing selection never silently disappears.
    const navValue = config.whatToShow.navigationCategory || '';
    const categoryOptions = [
        { value: '', label: `— ${tr('cfg_brands_nav_category_none', 'None')} —` },
        ...Array.from({ length: 20 }, (_, i) => i + 1).map((n) => ({
            value: `category_${n}`,
            label: shopFieldLabels[`category_${n}`] || `${tr('cfg_brands_category_prefix', 'Category')} ${n}`,
        })),
    ];
    if (navValue && !categoryOptions.some((o) => o.value === navValue)) {
        categoryOptions.push({ value: navValue, label: shopFieldLabels[navValue] || navValue });
    }
    const navSelected = categoryOptions.find((o) => o.value === navValue) || categoryOptions[0];

    return (
        <Stack spacing={2}>
            {/* What to show */}
            <ConfigCard anchor="cfg-card-brands-what" title={tr('cfg_brands_what_title', 'What to show')} description={tr('cfg_brands_what_desc', 'Decide which brands appear and how the page is organized.')} theme={theme}>
                <SettingRow
                    label={tr('cfg_brands_only_online', 'Only show brands with online products')}
                    help={tr('cfg_brands_only_online_help', "Brands without any products available online won't appear in the list")}
                    control={<Switch color="success" size="small" checked={config.whatToShow.onlyOnline} onChange={(e) => setSection('whatToShow')({ onlyOnline: e.target.checked })} />}
                />
                <SettingRow
                    label={tr('cfg_brands_nav_category', 'Category used for navigation')}
                    help={tr('cfg_brands_nav_category_help', 'When visitors click a brand, this is the category tree used to filter products')}
                    control={
                        <Autocomplete
                            size="small"
                            options={categoryOptions}
                            value={navSelected}
                            getOptionLabel={(o) => (o && o.label) || ''}
                            isOptionEqualToValue={(o, v) => o.value === v.value}
                            onChange={(_e, opt) => setSection('whatToShow')({ navigationCategory: opt ? opt.value : '' })}
                            disableClearable
                            sx={{ minWidth: 240 }}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    }
                />
            </ConfigCard>

            {/* Page layout — visual tiles + dependent settings */}
            <ConfigCard anchor="cfg-card-brands-layout" title={tr('cfg_brands_layout_title', 'Page layout')} description={tr('cfg_brands_layout_desc', 'Choose how brands are organized on the page.')} theme={theme}>
                <ToggleButtonGroup
                    exclusive
                    value={config.layout}
                    onChange={(_e, v) => v && setConfig(prev => ({ ...prev, layout: v }))}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                        gap: 1.5,
                        '& .MuiToggleButton-root': {
                            borderRadius: '12px !important',
                            border: '1px solid rgba(0,0,0,0.12) !important',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 1,
                            p: 2,
                            textTransform: 'none',
                            textAlign: 'left',
                            minHeight: 160,
                        },
                        '& .Mui-selected': {
                            backgroundColor: 'rgba(25,118,210,0.08) !important',
                            borderColor: 'rgba(25,118,210,0.6) !important',
                        },
                    }}
                >
                    {LAYOUTS.map((l) => (
                        <ToggleButton key={l.value} value={l.value}>
                            <Box sx={{ width: '100%', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
                                <LayoutTile value={l.value} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{tr(l.i18n_label, l.fallback_label)}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>{tr(l.i18n_desc, l.fallback_desc)}</Typography>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                {/* Settings rows beneath, each greyed / hinted when it doesn't apply */}
                <Stack spacing={1.25} divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
                    <SettingRow
                        label={tr('cfg_brands_display_continuous', 'Display style — continuous list')}
                        help={tr('cfg_brands_display_continuous_help', 'How brands appear in the continuous list view')}
                        disabled={!isContinuous}
                        disabledHint={tr('cfg_brands_only_when_continuous', "Only available when 'Continuous list' is selected above")}
                        control={
                            <TextField
                                select size="small"
                                value={config.settings.displayContinuous}
                                onChange={(e) => setSection('settings')({ displayContinuous: e.target.value })}
                                sx={{ minWidth: 160 }}
                            >
                                <MenuItem value="logo">{tr('cfg_brands_continuous_logo', 'Logo')}</MenuItem>
                                <MenuItem value="text">{tr('cfg_brands_continuous_text', 'Text')}</MenuItem>
                                <MenuItem value="both">{tr('cfg_brands_continuous_both', 'Logo + text')}</MenuItem>
                            </TextField>
                        }
                    />
                    <SettingRow
                        label={tr('cfg_brands_display_grouped', 'Display style — grouped by letter')}
                        help={tr('cfg_brands_display_grouped_help', 'How brands appear within each letter group')}
                        disabled={!isGrouped}
                        disabledHint={tr('cfg_brands_only_when_grouped', "Only available when 'Grouped by letter' or 'Grouped by letter (multi-column)' is selected above")}
                        control={
                            <TextField
                                select size="small"
                                value={config.settings.displayGrouped}
                                onChange={(e) => setSection('settings')({ displayGrouped: e.target.value })}
                                sx={{ minWidth: 160 }}
                            >
                                <MenuItem value="text">{tr('cfg_brands_grouped_text', 'Text')}</MenuItem>
                                <MenuItem value="logo">{tr('cfg_brands_grouped_logo', 'Logo')}</MenuItem>
                                <MenuItem value="both">{tr('cfg_brands_grouped_both', 'Logo + text')}</MenuItem>
                            </TextField>
                        }
                    />
                    <SettingRow
                        label={tr('cfg_brands_show_empty_letters', 'Show letters with no brands in the index')}
                        help={tr('cfg_brands_show_empty_letters_help', 'Greyed-out letters appear in the alphabet navigation even if no brand starts with them')}
                        disabled={!isGrouped}
                        disabledHint={tr('cfg_brands_only_when_grouped', "Only available when 'Grouped by letter' or 'Grouped by letter (multi-column)' is selected above")}
                        control={<Switch color="success" size="small" checked={config.settings.showLettersWithoutBrands} onChange={(e) => setSection('settings')({ showLettersWithoutBrands: e.target.checked })} />}
                    />
                    <SettingRow
                        label={tr('cfg_brands_letter_columns', 'Number of letter columns')}
                        help={tr('cfg_brands_letter_columns_help', 'How many letters appear side by side')}
                        disabled={!isMultiCol}
                        disabledHint={tr('cfg_brands_only_when_multicol', "Only available when 'Grouped by letter (multi-column)' is selected")}
                        control={
                            <TextField
                                type="number" size="small"
                                value={config.settings.letterColumns}
                                onChange={(e) => setSection('settings')({ letterColumns: Math.max(1, parseInt(e.target.value) || 1) })}
                                InputProps={{ endAdornment: <InputAdornment position="end">{tr('cfg_brands_columns_unit', 'columns')}</InputAdornment> }}
                                sx={{ width: 160 }}
                            />
                        }
                    />
                    <SettingRow
                        label={tr('cfg_brands_brands_per_letter', 'Brands per letter before "show more"')}
                        help={tr('cfg_brands_brands_per_letter_help', "Limits how many brands are visible per letter; visitors click 'show more' to see the rest")}
                        disabled={!isMultiCol}
                        disabledHint={tr('cfg_brands_only_when_multicol', "Only available when 'Grouped by letter (multi-column)' is selected")}
                        control={
                            <TextField
                                type="number" size="small"
                                value={config.settings.brandsPerLetter}
                                onChange={(e) => setSection('settings')({ brandsPerLetter: Math.max(0, parseInt(e.target.value) || 0) })}
                                InputProps={{ endAdornment: <InputAdornment position="end">{tr('cfg_brands_brands_unit', 'brands')}</InputAdornment> }}
                                sx={{ width: 160 }}
                            />
                        }
                    />
                </Stack>
            </ConfigCard>

            {/* Text styles — Desktop + Mobile two columns */}
            <ConfigCard anchor="cfg-card-brands-text-styles" title={tr('cfg_brands_text_styles_title', 'Text styles')} description={tr('cfg_brands_text_styles_desc', 'Style the brand names, alphabet navigation, and letter headers. Set sizes per device.')} theme={theme}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {[
                        { key: 'desktop', label: tr('cfg_brands_text_desktop', '🖥  Desktop') },
                        { key: 'mobile',  label: tr('cfg_brands_text_mobile',  '📱  Mobile') },
                    ].map(({ key, label }) => (
                        <Box key={key} sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: 'rgba(0,0,0,0.02)', border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5, fontSize: '0.8rem' }}>{label}</Typography>
                            <Stack spacing={1.5}>
                                <TextStyleBlock
                                    label={tr('cfg_brands_text_brand_name',     'Brand name')}
                                    help={tr('cfg_brands_text_brand_name_h',    'The full written brand name in the list')}
                                    value={config.textStyles[key].brandName}
                                    onChange={(v) => setNested('textStyles', key)({ brandName: v })}
                                />
                                <TextStyleBlock
                                    label={tr('cfg_brands_text_alphabet_nav',   'Alphabet navigation')}
                                    help={tr('cfg_brands_text_alphabet_nav_h', 'The A B C… shortcuts at the top of the page')}
                                    value={config.textStyles[key].alphabetNav}
                                    onChange={(v) => setNested('textStyles', key)({ alphabetNav: v })}
                                />
                                <TextStyleBlock
                                    label={tr('cfg_brands_text_letter_heading', 'Letter heading')}
                                    help={tr('cfg_brands_text_letter_heading_h', 'The big letter (A, B, C…) shown above each group of brands')}
                                    value={config.textStyles[key].letterHeading}
                                    onChange={(v) => setNested('textStyles', key)({ letterHeading: v })}
                                />
                            </Stack>
                        </Box>
                    ))}
                </Box>
            </ConfigCard>
        </Stack>
    );
};

MerkenTab.propTypes = { theme: PropTypes.string, t: PropTypes.func, config: PropTypes.object, onConfigChange: PropTypes.func, shopFieldLabels: PropTypes.object };
export default MerkenTab;
